import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  SmartCompanyInput,
  type RegistryCompany,
} from "@/components/SmartCompanyInput";
import { DivergenceIndicator } from "@/components/DivergenceIndicator";
import {
  SandboxModeToggle,
  useSandboxMode,
} from "@/components/SandboxModeToggle";
import {
  Building2,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Archive,
  ArchiveRestore,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function Companies() {
  return (
    <DashboardLayout>
      <CompaniesContent />
    </DashboardLayout>
  );
}

function CompaniesContent() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );
  const [showArchived, setShowArchived] = useState(false);

  // Track registry data for divergence indicators
  const [registryData, setRegistryData] = useState<RegistryCompany | null>(
    null
  );

  const [canton, setCanton] = useState<string>("ZH");

  const [formData, setFormData] = useState({
    name: "",
    uid: "",
    legalForm: "",
    address: "",
    city: "",
    postalCode: "",
    country: "Switzerland",
    contactEmail: "",
    contactPhone: "",
    taxNumber: "",
    vatNumber: "",
  });

  const [formErrors, setFormErrors] = useState<{
    name?: string;
    uid?: string;
  }>({});

  const validateUid = (uid: string): string | undefined => {
    if (!uid) return undefined;
    return /^CHE-\d{3}\.\d{3}\.\d{3}$/.test(uid)
      ? undefined
      : "Must be format CHE-XXX.XXX.XXX";
  };

  const sandboxMode = useSandboxMode();

  const {
    data: companies,
    isLoading,
    refetch,
  } = trpc.company.list.useQuery({ includeArchived: showArchived });

  const createMutation = trpc.company.create.useMutation({
    onSuccess: () => {
      toast.success("Company created successfully");
      setIsCreateDialogOpen(false);
      resetForm();
      refetch();
    },
    onError: error => {
      toast.error("Failed to create company", {
        description: error.message,
      });
    },
  });

  const updateMutation = trpc.company.update.useMutation({
    onSuccess: () => {
      toast.success("Company updated successfully");
      setIsEditDialogOpen(false);
      resetForm();
      refetch();
    },
    onError: error => {
      toast.error("Failed to update company", {
        description: error.message,
      });
    },
  });

  const archiveMutation = trpc.company.archive.useMutation({
    onSuccess: () => {
      toast.success("Company archived successfully");
      setIsDeleteDialogOpen(false);
      setSelectedCompanyId(null);
      refetch();
    },
    onError: error => {
      toast.error("Failed to archive company", {
        description: error.message,
      });
    },
  });

  const restoreMutation = trpc.company.restore.useMutation({
    onSuccess: () => {
      toast.success("Company restored successfully");
      refetch();
    },
    onError: error => {
      toast.error("Failed to restore company", {
        description: error.message,
      });
    },
  });

  const deleteMutation = trpc.company.delete.useMutation({
    onSuccess: () => {
      toast.success("Company permanently deleted");
      setIsDeleteDialogOpen(false);
      setSelectedCompanyId(null);
      refetch();
    },
    onError: error => {
      toast.error("Failed to delete company", {
        description: error.message,
      });
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      uid: "",
      legalForm: "",
      address: "",
      city: "",
      postalCode: "",
      country: "Switzerland",
      contactEmail: "",
      contactPhone: "",
      taxNumber: "",
      vatNumber: "",
    });
    setCanton("ZH");
    setRegistryData(null);
    setSelectedCompanyId(null);
    setFormErrors({});
  };

  // Auto-fill form when a company is selected from SmartCompanyInput
  const handleRegistrySelect = (company: RegistryCompany) => {
    // Store registry data for divergence tracking
    setRegistryData(company);

    // Registry returns nested address object
    const addressData = company.address || {};
    setFormData({
      name: company.name,
      uid: company.uid || "",
      legalForm: company.legalForm || "",
      address: addressData.street || "",
      city: addressData.city || "",
      postalCode: addressData.zip || "",
      country: addressData.country || "Switzerland",
      contactEmail: "",
      contactPhone: "",
      taxNumber: "",
      vatNumber: company.vatNumber || "",
    });
    // Update canton from address
    if (addressData.canton) {
      setCanton(addressData.canton);
    }
    toast.success("Company details loaded from registry");
  };

  const handleCreate = () => {
    if (!formData.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    // Transform flat formData to nested structure expected by server
    createMutation.mutate({
      name: formData.name,
      uid: formData.uid || undefined,
      legalForm: formData.legalForm || undefined,
      address: {
        street: formData.address || undefined,
        city: formData.city || undefined,
        zip: formData.postalCode || undefined,
        canton: canton || undefined,
        country: formData.country || undefined,
      },
      contact: {
        email: formData.contactEmail || undefined,
        phone: formData.contactPhone || undefined,
      },
      taxInfo: {
        vatNumber: formData.vatNumber || undefined,
        canton: canton || undefined,
      },
    });
  };

  const handleEdit = (company: any) => {
    setSelectedCompanyId(company.id);
    // Extract nested data from company object into flat formData
    const addressData = company.address || {};
    const contactData = company.contact || {};
    const taxInfoData = company.taxInfo || {};
    setFormData({
      name: company.name || "",
      uid: company.uid || "",
      legalForm: company.legalForm || "",
      address: addressData.street || "",
      city: addressData.city || "",
      postalCode: addressData.zip || "",
      country: addressData.country || "Switzerland",
      contactEmail: contactData.email || "",
      contactPhone: contactData.phone || "",
      taxNumber: "", // Not stored in taxInfo, keeping for form compatibility
      vatNumber: taxInfoData.vatNumber || "",
    });
    // Set canton from address or taxInfo
    if (addressData.canton) {
      setCanton(addressData.canton);
    } else if (taxInfoData.canton) {
      setCanton(taxInfoData.canton);
    }
    setIsEditDialogOpen(true);
  };

  const handleUpdate = () => {
    if (!selectedCompanyId) return;
    if (!formData.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    // Transform flat formData to nested structure expected by server
    updateMutation.mutate({
      id: selectedCompanyId,
      name: formData.name,
      uid: formData.uid || undefined,
      legalForm: formData.legalForm || undefined,
      address: {
        street: formData.address || undefined,
        city: formData.city || undefined,
        zip: formData.postalCode || undefined,
        canton: canton || undefined,
        country: formData.country || undefined,
      },
      contact: {
        email: formData.contactEmail || undefined,
        phone: formData.contactPhone || undefined,
      },
      taxInfo: {
        vatNumber: formData.vatNumber || undefined,
        canton: canton || undefined,
      },
    });
  };

  const handleDelete = (companyId: number) => {
    setSelectedCompanyId(companyId);
    setIsDeleteDialogOpen(true);
  };

  const handleRestore = (companyId: number) => {
    restoreMutation.mutate({ id: companyId });
  };

  // Archive (soft delete) or hard delete based on sandbox mode
  const confirmDelete = (hardDelete: boolean = false) => {
    if (!selectedCompanyId) return;

    if (hardDelete && sandboxMode) {
      deleteMutation.mutate({ id: selectedCompanyId, sandboxMode: true });
    } else {
      archiveMutation.mutate({ id: selectedCompanyId });
    }
  };

  // Check if a company is archived
  const isCompanyArchived = (company: any) => {
    return company.archivedAt !== null && company.archivedAt !== undefined;
  };

  if (isLoading) {
    return (
      <div className="container py-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Companies</h1>
          <p className="text-muted-foreground mt-2">
            Manage your company profiles and tax information
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="show-archived"
              checked={showArchived}
              onCheckedChange={setShowArchived}
            />
            <Label
              htmlFor="show-archived"
              className="text-sm cursor-pointer flex items-center gap-1"
            >
              {showArchived ? (
                <Eye className="h-4 w-4" />
              ) : (
                <EyeOff className="h-4 w-4" />
              )}
              Show Archived
            </Label>
          </div>
          <SandboxModeToggle compact />
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Company
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your Companies</CardTitle>
          <CardDescription>
            {companies?.length || 0} company(ies) registered
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!companies || companies.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No companies yet</h3>
              <p className="text-muted-foreground mb-4">
                Get started by creating your first company profile
              </p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Company
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>UID</TableHead>
                  <TableHead>Legal Form</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map(company => {
                  const archived = isCompanyArchived(company);
                  return (
                    <TableRow
                      key={company.id}
                      className={archived ? "opacity-60" : ""}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {company.name}
                          {archived && (
                            <Badge variant="secondary" className="text-xs">
                              Archived
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{company.uid || "-"}</TableCell>
                      <TableCell>{company.legalForm || "-"}</TableCell>
                      <TableCell>{company.address?.city || "-"}</TableCell>
                      <TableCell>{company.contact?.email || "-"}</TableCell>
                      <TableCell>
                        {archived ? (
                          <span className="text-muted-foreground text-sm">
                            Archived
                          </span>
                        ) : (
                          <span className="text-[var(--success)] text-sm">
                            Active
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {archived ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRestore(company.id)}
                              disabled={restoreMutation.isPending}
                            >
                              <ArchiveRestore className="h-4 w-4 text-[var(--success)]" />
                            </Button>
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(company)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(company.id)}
                              >
                                <Archive className="h-4 w-4 text-[var(--warning)]" />
                              </Button>
                            </>
                          )}
                          {sandboxMode && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(company.id)}
                              title="Permanently delete (Sandbox mode)"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Company</DialogTitle>
            <DialogDescription>
              Add a new company profile to manage its tax data separately
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Smart Company Search - searches by name, UID, or MWST number */}
            <div className="space-y-2">
              <Label>Search Swiss Commercial Register (Zefix)</Label>
              <SmartCompanyInput
                onSelect={handleRegistrySelect}
                defaultCanton={canton}
                placeholder="Enter company name, UID (CHE-XXX.XXX.XXX), or MWST number..."
              />
              <p className="text-sm text-muted-foreground">
                Search by company name (3+ chars), UID, or MWST number. Fields
                auto-fill on selection.
              </p>
            </div>

            {/* Company Details - shows divergence indicators when registry data exists */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2">
                  Company Name *
                  <DivergenceIndicator
                    show={!!registryData && formData.name !== registryData.name}
                    registryValue={registryData?.name}
                    fieldLabel="Name"
                  />
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={e => {
                    setFormData({ ...formData, name: e.target.value });
                    if (formErrors.name)
                      setFormErrors({ ...formErrors, name: undefined });
                  }}
                  onBlur={e => {
                    if (!e.target.value.trim())
                      setFormErrors({
                        ...formErrors,
                        name: "Company name is required",
                      });
                  }}
                  placeholder="e.g., Acme GmbH"
                  aria-invalid={!!formErrors.name}
                />
                {formErrors.name && (
                  <p className="text-[var(--destructive)] text-xs">
                    {formErrors.name}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="uid" className="flex items-center gap-2">
                  UID Number
                  <DivergenceIndicator
                    show={!!registryData && formData.uid !== registryData.uid}
                    registryValue={registryData?.uid}
                    fieldLabel="UID"
                  />
                </Label>
                <Input
                  id="uid"
                  value={formData.uid}
                  onChange={e => {
                    setFormData({ ...formData, uid: e.target.value });
                    if (formErrors.uid)
                      setFormErrors({ ...formErrors, uid: undefined });
                  }}
                  onBlur={e => {
                    const err = validateUid(e.target.value);
                    if (err) setFormErrors({ ...formErrors, uid: err });
                  }}
                  placeholder="CHE-123.456.789"
                  aria-invalid={!!formErrors.uid}
                />
                {formErrors.uid && (
                  <p className="text-[var(--destructive)] text-xs">
                    {formErrors.uid}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="legalForm">Legal Form</Label>
                <Input
                  id="legalForm"
                  value={formData.legalForm}
                  onChange={e =>
                    setFormData({ ...formData, legalForm: e.target.value })
                  }
                  placeholder="e.g., GmbH, AG, Einzelfirma"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="taxNumber">Tax Number</Label>
                <Input
                  id="taxNumber"
                  value={formData.taxNumber}
                  onChange={e =>
                    setFormData({ ...formData, taxNumber: e.target.value })
                  }
                  placeholder="Tax identification number"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={e =>
                  setFormData({ ...formData, address: e.target.value })
                }
                placeholder="Street address"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="postalCode">Postal Code</Label>
                <Input
                  id="postalCode"
                  value={formData.postalCode}
                  onChange={e =>
                    setFormData({ ...formData, postalCode: e.target.value })
                  }
                  placeholder="8000"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={e =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                  placeholder="Zürich"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={e =>
                    setFormData({ ...formData, country: e.target.value })
                  }
                  placeholder="Switzerland"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contactEmail">Contact Email</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  value={formData.contactEmail}
                  onChange={e =>
                    setFormData({ ...formData, contactEmail: e.target.value })
                  }
                  placeholder="contact@company.ch"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactPhone">Contact Phone</Label>
                <Input
                  id="contactPhone"
                  value={formData.contactPhone}
                  onChange={e =>
                    setFormData({ ...formData, contactPhone: e.target.value })
                  }
                  placeholder="+41 44 123 45 67"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="vatNumber">VAT Number</Label>
              <Input
                id="vatNumber"
                value={formData.vatNumber}
                onChange={e =>
                  setFormData({ ...formData, vatNumber: e.target.value })
                }
                placeholder="CHE-123.456.789 MWST"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateDialogOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Company"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Company</DialogTitle>
            <DialogDescription>
              Update company information and tax details
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Same form fields as create dialog */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Company Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={e => {
                    setFormData({ ...formData, name: e.target.value });
                    if (formErrors.name)
                      setFormErrors({ ...formErrors, name: undefined });
                  }}
                  onBlur={e => {
                    if (!e.target.value.trim())
                      setFormErrors({
                        ...formErrors,
                        name: "Company name is required",
                      });
                  }}
                  placeholder="e.g., Acme GmbH"
                  aria-invalid={!!formErrors.name}
                />
                {formErrors.name && (
                  <p className="text-[var(--destructive)] text-xs">
                    {formErrors.name}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-uid">UID Number</Label>
                <Input
                  id="edit-uid"
                  value={formData.uid}
                  onChange={e => {
                    setFormData({ ...formData, uid: e.target.value });
                    if (formErrors.uid)
                      setFormErrors({ ...formErrors, uid: undefined });
                  }}
                  onBlur={e => {
                    const err = validateUid(e.target.value);
                    if (err) setFormErrors({ ...formErrors, uid: err });
                  }}
                  placeholder="CHE-123.456.789"
                  aria-invalid={!!formErrors.uid}
                />
                {formErrors.uid && (
                  <p className="text-[var(--destructive)] text-xs">
                    {formErrors.uid}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-legalForm">Legal Form</Label>
                <Input
                  id="edit-legalForm"
                  value={formData.legalForm}
                  onChange={e =>
                    setFormData({ ...formData, legalForm: e.target.value })
                  }
                  placeholder="e.g., GmbH, AG, Einzelfirma"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-taxNumber">Tax Number</Label>
                <Input
                  id="edit-taxNumber"
                  value={formData.taxNumber}
                  onChange={e =>
                    setFormData({ ...formData, taxNumber: e.target.value })
                  }
                  placeholder="Tax identification number"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-address">Address</Label>
              <Textarea
                id="edit-address"
                value={formData.address}
                onChange={e =>
                  setFormData({ ...formData, address: e.target.value })
                }
                placeholder="Street address"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-postalCode">Postal Code</Label>
                <Input
                  id="edit-postalCode"
                  value={formData.postalCode}
                  onChange={e =>
                    setFormData({ ...formData, postalCode: e.target.value })
                  }
                  placeholder="8000"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-city">City</Label>
                <Input
                  id="edit-city"
                  value={formData.city}
                  onChange={e =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                  placeholder="Zürich"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-country">Country</Label>
                <Input
                  id="edit-country"
                  value={formData.country}
                  onChange={e =>
                    setFormData({ ...formData, country: e.target.value })
                  }
                  placeholder="Switzerland"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-contactEmail">Contact Email</Label>
                <Input
                  id="edit-contactEmail"
                  type="email"
                  value={formData.contactEmail}
                  onChange={e =>
                    setFormData({ ...formData, contactEmail: e.target.value })
                  }
                  placeholder="contact@company.ch"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-contactPhone">Contact Phone</Label>
                <Input
                  id="edit-contactPhone"
                  value={formData.contactPhone}
                  onChange={e =>
                    setFormData({ ...formData, contactPhone: e.target.value })
                  }
                  placeholder="+41 44 123 45 67"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-vatNumber">VAT Number</Label>
              <Input
                id="edit-vatNumber"
                value={formData.vatNumber}
                onChange={e =>
                  setFormData({ ...formData, vatNumber: e.target.value })
                }
                placeholder="CHE-123.456.789 MWST"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Company"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive/Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {sandboxMode ? "Delete Company" : "Archive Company"}
            </DialogTitle>
            <DialogDescription>
              {sandboxMode ? (
                <>
                  <span className="block mb-2">
                    <strong>Sandbox Mode is enabled.</strong> You can
                    permanently delete this company.
                  </span>
                  Are you sure? This action cannot be undone. All associated
                  transactions and data will be permanently deleted.
                </>
              ) : (
                <>
                  This company will be archived and hidden from your active
                  list. You can restore it later from the "Show Archived" view.
                  No data will be lost.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setSelectedCompanyId(null);
              }}
            >
              Cancel
            </Button>
            {sandboxMode ? (
              <>
                <Button
                  variant="secondary"
                  onClick={() => confirmDelete(false)}
                  disabled={archiveMutation.isPending}
                >
                  {archiveMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Archiving...
                    </>
                  ) : (
                    <>
                      <Archive className="h-4 w-4 mr-2" />
                      Archive Only
                    </>
                  )}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => confirmDelete(true)}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Permanently Delete
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button
                variant="default"
                onClick={() => confirmDelete(false)}
                disabled={archiveMutation.isPending}
              >
                {archiveMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Archiving...
                  </>
                ) : (
                  <>
                    <Archive className="h-4 w-4 mr-2" />
                    Archive Company
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
